import { h, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { Text, Localizer } from 'preact-i18n';
import { route } from 'preact-router';
import { appConfirm } from '../../lib/dialogs';
import { beginDialogAction } from '../../lib/dialogAction';

/**
 * Determine whether course registration is currently open. Mirrors the backend
 * checks in backend/app/routes/api/courses/post-date-signup.js so the button the
 * user sees matches what the server will accept. Respecting the DATE WINDOW (not
 * just the `registrationIsOpen` flag) is what prevents an active signup button on
 * a course whose window has passed — every such click failed with REGISTRATION_ENDED.
 * @param {Object} course
 * @param {Date} now
 * @returns {{ open: boolean, reason: ('closed'|'notStarted'|'ended'|null) }}
 */
export function getRegistrationStatus(course = {}, now = new Date()) {
    if (!course.registrationIsOpen) {
        return { open: false, reason: 'closed' };
    }
    if (course.registrationStartDate && now < new Date(course.registrationStartDate)) {
        return { open: false, reason: 'notStarted' };
    }
    if (course.registrationEndDate && now > new Date(course.registrationEndDate)) {
        return { open: false, reason: 'ended' };
    }
    return { open: true, reason: null };
}

@observer
class CourseSignupCard extends Component {
    constructor(props) {
        super(props);
        this.state = {
            loading: false,
            showSignupForm: false,
            showNameForm: false,
            nameFirstname: '',
            nameLastname: '',
            fieldValues: {},
            fieldErrors: {},
        };
    }

    initFieldValues = () => {
        const { course } = this.props;
        const signupFields = course.signupFields || [];
        const fieldValues = {};
        signupFields.forEach(field => {
            fieldValues[field.name] = '';
        });
        this.setState({ fieldValues, fieldErrors: {} });
    }

    handleFieldChange = (e) => {
        const { name, value } = e.target;
        this.setState(prevState => ({
            fieldValues: {
                ...prevState.fieldValues,
                [name]: value,
            },
            fieldErrors: {
                ...prevState.fieldErrors,
                [name]: null,
            },
        }));
    }

    validateFields = () => {
        const { course } = this.props;
        const { fieldValues } = this.state;
        const signupFields = course.signupFields || [];
        const errors = {};
        let isValid = true;

        signupFields.forEach(field => {
            if (field.required && (!fieldValues[field.name] || fieldValues[field.name].trim() === '')) {
                errors[field.name] = `${field.label} er påkrevd`;
                isValid = false;
            }
        });

        this.setState({ fieldErrors: errors });
        return isValid;
    }

    startSignup = () => {
        this.initFieldValues();
        this.setState({ showSignupForm: true });
    }

    cancelSignupForm = () => {
        this.setState({ showSignupForm: false, fieldValues: {}, fieldErrors: {} });
    }

    getErrorMessage(errorCode, response) {
        const errorMessages = {
            COURSE_FULL: 'Beklager, kurset er nå fullt. Listen oppdateres.',
            ALREADY_SIGNED_UP: 'Du er allerede påmeldt dette kurset.',
            REGISTRATION_CLOSED: 'Registreringen er stengt.',
            REGISTRATION_NOT_STARTED: 'Registreringen har ikke startet enda.',
            REGISTRATION_ENDED: 'Registreringen er avsluttet.',
            UNAUTHORIZED: 'Du må være logget inn for å melde deg på.',
            COURSE_STARTED: 'Kan ikke avmelde etter at kurset har startet.',
            NOT_SIGNED_UP: 'Du er ikke påmeldt dette kurset.',
            INCOMPLETE_PROFILE: 'Du må fylle inn fullt navn før du kan melde deg på.',
        };
        return errorMessages[errorCode] || response?.message || 'Kunne ikke melde på. Prøv igjen.';
    }

    userNeedsName = () => {
        const { userStore } = this.props.stores;
        const { user = {} } = userStore;
        return !user.firstname || !user.lastname;
    }

    // Not logged in: remember the course so login returns here, then send to login
    // (which also offers register). Mirrors components/convention/view-signup.js.
    promptLogin = () => {
        const { appState } = this.props.stores;
        const { course } = this.props;
        appState.setWantedPage(`/course/${course.id}`);
        route('/login');
    }

    startNameForm = () => {
        const { userStore } = this.props.stores;
        const { user = {} } = userStore;
        this.setState({
            showNameForm: true,
            nameFirstname: user.firstname || '',
            nameLastname: user.lastname || '',
        });
    }

    cancelNameForm = () => {
        this.setState({ showNameForm: false, nameFirstname: '', nameLastname: '' });
    }

    handleNameChange = (e) => {
        const { name, value } = e.target;
        this.setState({ [name]: value });
    }

    handleNameSubmit = async () => {
        const { userStore, appState } = this.props.stores;
        const { user = {} } = userStore;
        const { nameFirstname, nameLastname } = this.state;
        const { course } = this.props;
        const signupFields = course.signupFields || [];
        const hasSignupFields = signupFields.length > 0;

        if (!nameFirstname.trim() || !nameLastname.trim()) {
            appState.addToast({
                type: 'error',
                message: 'Du må fylle inn både fornavn og etternavn.',
            }, 5000);
            return;
        }

        this.setState({ loading: true });

        try {
            await userStore.updateUser(user.email, {
                firstname: nameFirstname.trim(),
                lastname: nameLastname.trim(),
            });

            this.setState({ showNameForm: false, nameFirstname: '', nameLastname: '' });

            if (hasSignupFields) {
                this.startSignup();
            } else {
                await this.handleSignUp();
            }
        } catch (err) {
            appState.addToast({
                type: 'error',
                message: `Kunne ikke lagre navn: ${err.message}`,
            }, 5000);
        } finally {
            this.setState({ loading: false });
        }
    }

    handleSignUp = async () => {
        const { courseStore, userStore, appState } = this.props.stores;
        const { course } = this.props;
        const { user = {} } = userStore;
        const { callback } = this.props;
        const { fieldValues } = this.state;
        const signupFields = course.signupFields || [];

        if (!user || !user.id) {
            this.promptLogin();
            return;
        }

        // Validate fields if there are any
        if (signupFields.length > 0 && !this.validateFields()) {
            return;
        }

        this.setState({ loading: true });

        try {
            const response = await courseStore.signUpForCourse(fieldValues);

            if (response && response.status === 200) {
                appState.addToast({
                    type: 'success',
                    message: 'Du er nå påmeldt kurset!',
                }, 5000);

                this.setState({ showSignupForm: false, fieldValues: {}, fieldErrors: {} });

                if (callback) {
                    await callback();
                }
            } else if (response && response.errorCode) {
                const errorMessage = this.getErrorMessage(response.errorCode, response);
                appState.addToast({
                    type: 'error',
                    message: errorMessage,
                }, 5000);
            } else if (response) {
                appState.addToast({
                    type: 'error',
                    message: `Kunne ikke melde på: ${response?.message || 'Ukjent feil'}`,
                }, 5000);
            }
        } catch (err) {
            appState.addToast({
                type: 'error',
                message: `Kunne ikke melde på: ${err.message}`,
            }, 5000);
        } finally {
            this.setState({ loading: false });
        }
    }

    handleCancel = async () => {
        const dialogAction = beginDialogAction(this, 'handleCancel', () => [
            this.props.stores.courseStore.course?.id,
        ]);
        if (!dialogAction) return false;
        try {
            const { courseStore, appState } = this.props.stores;
            const { callback } = this.props;

            if (!((await appConfirm('Er du sikker på at du vil melde deg av kurset?')) && dialogAction.isCurrent())) {
                return;
            }

            this.setState({ loading: true });

            try {
                const response = await courseStore.cancelCourseSignup();

                if (response && response.status === 200) {
                    appState.addToast({
                        type: 'success',
                        message: 'Du er nå avmeldt kurset.',
                    }, 5000);

                    if (callback) {
                        await callback();
                    }
                } else if (response && response.errorCode) {
                    const errorMessage = this.getErrorMessage(response.errorCode, response);
                    appState.addToast({
                        type: 'error',
                        message: errorMessage,
                    }, 5000);
                } else if (response) {
                    appState.addToast({
                        type: 'error',
                        message: `Kunne ikke melde av: ${response?.message || 'Ukjent feil'}`,
                    }, 5000);
                }
            } catch (err) {
                appState.addToast({
                    type: 'error',
                    message: `Kunne ikke melde av: ${err.message}`,
                }, 5000);
            } finally {
                this.setState({ loading: false });
            }
        } finally {
            dialogAction.finish();
        }
    }

    renderNameForm() {
        const { loading, nameFirstname, nameLastname } = this.state;

        return (
            <div class='card border-primary'>
                <div class='card-body'>
                    <h5 class='card-title mb-3'>
                        <i class='fa-solid fa-user text-primary me-2' />
                        Fyll inn navnet ditt
                    </h5>
                    <p class='text-body-secondary mb-3'>
                        Du må fylle inn fullt navn før du kan melde deg på kurset.
                    </p>

                    <form onSubmit={(e) => { e.preventDefault(); this.handleNameSubmit(); }}>
                        <div class='mb-3'>
                            <label class='form-label'>
                                Fornavn <span class='text-danger ms-1'>*</span>
                            </label>
                            <input
                                type='text'
                                class='form-control'
                                name='nameFirstname'
                                value={nameFirstname}
                                onInput={this.handleNameChange}
                                required
                            />
                        </div>
                        <div class='mb-3'>
                            <label class='form-label'>
                                Etternavn <span class='text-danger ms-1'>*</span>
                            </label>
                            <input
                                type='text'
                                class='form-control'
                                name='nameLastname'
                                value={nameLastname}
                                onInput={this.handleNameChange}
                                required
                            />
                        </div>

                        <div class='d-flex gap-2 mt-3'>
                            <button
                                type='submit'
                                class='btn btn-primary rounded-pill'
                                disabled={loading}
                            >
                                {loading ? (
                                    <span class='spinner-border spinner-border-sm' role='status'>
                                        <span class='visually-hidden'>Loading...</span>
                                    </span>
                                ) : (
                                    <>
                                        <i class='fa-solid fa-check me-1' />
                                        Lagre og meld på
                                    </>
                                )}
                            </button>
                            <button
                                type='button'
                                class='btn btn-outline-secondary rounded-pill'
                                onClick={this.cancelNameForm}
                                disabled={loading}
                            >
                                <Text id='course.cancel'>Avbryt</Text>
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        );
    }

    renderSignupForm() {
        const { loading, fieldValues, fieldErrors } = this.state;
        const { course } = this.props;
        const signupFields = course.signupFields || [];

        return (
            <div class='card border-primary'>
                <div class='card-body'>
                    <h5 class='card-title mb-3'>
                        <i class='fa-solid fa-user-plus text-primary me-2' />
                        <Text id='course.signUpForCourse'>Meld deg på kurset</Text>
                    </h5>

                    <form onSubmit={(e) => { e.preventDefault(); this.handleSignUp(); }}>
                        {signupFields.map(field => (
                            <div class='mb-3' key={field.name}>
                                <label class='form-label'>
                                    {field.label}
                                    {field.required && <span class='text-danger ms-1'>*</span>}
                                </label>
                                {field.type === 'select' ? (
                                    <select
                                        class={`form-select ${fieldErrors[field.name] ? 'is-invalid' : ''}`}
                                        name={field.name}
                                        value={fieldValues[field.name] || ''}
                                        onChange={this.handleFieldChange}
                                        required={field.required}
                                    >
                                        <option value=''>Velg...</option>
                                        {(field.options || '').split(',').map(opt => (
                                            <option key={opt.trim()} value={opt.trim()}>{opt.trim()}</option>
                                        ))}
                                    </select>
                                ) : field.type === 'textarea' ? (
                                    <textarea
                                        class={`form-control ${fieldErrors[field.name] ? 'is-invalid' : ''}`}
                                        name={field.name}
                                        value={fieldValues[field.name] || ''}
                                        onChange={this.handleFieldChange}
                                        required={field.required}
                                        rows={3}
                                    />
                                ) : (
                                    <input
                                        type={field.type || 'text'}
                                        class={`form-control ${fieldErrors[field.name] ? 'is-invalid' : ''}`}
                                        name={field.name}
                                        value={fieldValues[field.name] || ''}
                                        onChange={this.handleFieldChange}
                                        required={field.required}
                                    />
                                )}
                                {fieldErrors[field.name] && (
                                    <div class='invalid-feedback'>{fieldErrors[field.name]}</div>
                                )}
                            </div>
                        ))}

                        <div class='d-flex gap-2 mt-3'>
                            <button
                                type='submit'
                                class='btn btn-primary rounded-pill'
                                disabled={loading}
                            >
                                {loading ? (
                                    <span class='spinner-border spinner-border-sm' role='status'>
                                        <span class='visually-hidden'>Loading...</span>
                                    </span>
                                ) : (
                                    <>
                                        <i class='fa-solid fa-check me-1' />
                                        <Text id='course.confirmSignup'>Bekreft påmelding</Text>
                                    </>
                                )}
                            </button>
                            <button
                                type='button'
                                class='btn btn-outline-secondary rounded-pill'
                                onClick={this.cancelSignupForm}
                                disabled={loading}
                            >
                                <Text id='course.cancel'>Avbryt</Text>
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        );
    }

    render() {
        const { loading, showSignupForm, showNameForm } = this.state;
        const { course } = this.props;
        const { courseStore, userStore } = this.props.stores;
        const { user = {} } = userStore;

        // Registration window check — mirror the backend (post-date-signup.js) so the
        // button the user sees matches what the server will accept. A stale
        // registrationIsOpen flag alone is not enough: a course whose date window has
        // passed must show "ended", not an active button that fails with REGISTRATION_ENDED.
        const registration = getRegistrationStatus(course);
        if (!registration.open) {
            const closedMessage = {
                notStarted: <Text id='course.registrationNotStarted'>Registration has not started yet</Text>,
                ended: <Text id='course.registrationEnded'>Registration has ended</Text>,
                closed: <Text id='course.registrationClosed'>Registreringen er stengt</Text>,
            }[registration.reason];
            const closedIcon = registration.reason === 'notStarted' ? 'fa-hourglass-start' : 'fa-lock';
            return (
                <div class='card border-secondary'>
                    <div class='card-body text-center'>
                        <i class={`fa-solid ${closedIcon} fa-2x text-secondary mb-2`} />
                        <p class='mb-0 text-body-secondary'>
                            {closedMessage}
                        </p>
                    </div>
                </div>
            );
        }

        const availability = courseStore.getCourseAvailability(user.id);
        const { slotsUsed, slotsTotal, isSignedUp, userSignup, isFull, canSignUp } = availability;

        const hasSlotLimit = slotsTotal > 0;
        const slotsRemaining = hasSlotLimit ? slotsTotal - slotsUsed : null;
        const signupFields = course.signupFields || [];
        const hasSignupFields = signupFields.length > 0;

        // Show name form if user needs to fill in their name
        if (showNameForm) {
            return this.renderNameForm();
        }

        // Show signup form if there are custom fields and user clicked signup
        if (showSignupForm && hasSignupFields && canSignUp && !isSignedUp) {
            return this.renderSignupForm();
        }

        return (
            <div class={`card ${isSignedUp ? 'border-success' : isFull ? 'border-danger' : 'border-primary'}`}>
                <div class='card-body'>
                    <div class='d-flex justify-content-between align-items-start'>
                        <div>
                            <h5 class='card-title mb-2'>
                                {isSignedUp ? (
                                    <>
                                        <i class='fa-solid fa-check-circle text-success me-2' />
                                        <Text id='course.youAreSignedUp'>Du er påmeldt!</Text>
                                    </>
                                ) : (
                                    <>
                                        <i class='fa-solid fa-user-plus text-primary me-2' />
                                        <Text id='course.signUpForCourse'>Meld deg på kurset</Text>
                                    </>
                                )}
                            </h5>

                            {/* Price info */}
                            {course.price > 0 && (
                                <p class='card-text mb-2'>
                                    <i class='fa-solid fa-coins me-2 text-body-secondary' />
                                    <strong>{util.format(course.price, 0)} kr</strong>
                                    <span class='text-body-secondary ms-2'>
                                        (inkl. mva)
                                    </span>
                                </p>
                            )}

                            {/* Slots info */}
                            {hasSlotLimit && (
                                <p class='card-text mb-2'>
                                    <i class='fa-solid fa-users me-2 text-body-secondary' />
                                    {slotsUsed} / {slotsTotal} <Text id='course.participants'>deltakere</Text>
                                    {slotsRemaining > 0 && !isFull && (
                                        <span class={`ms-2 ${slotsRemaining <= 3 ? 'text-warning' : 'text-success'}`}>
                                            ({slotsRemaining} <Text id='course.slotsAvailable'>ledige</Text>)
                                        </span>
                                    )}
                                    {isFull && !isSignedUp && (
                                        <span class='text-danger ms-2'>
                                            (<Text id='course.full'>Fullt</Text>)
                                        </span>
                                    )}
                                </p>
                            )}

                            {/* Sale info if signed up */}
                            {isSignedUp && userSignup?.saleId && (
                                <p class='card-text text-body-secondary small mb-0'>
                                    <i class='fa-solid fa-receipt me-1' />
                                    Salg #{userSignup.saleNumber || userSignup.saleId}
                                </p>
                            )}

                            {/* Show custom field values if signed up */}
                            {isSignedUp && userSignup?.signupData && Object.keys(userSignup.signupData).length > 0 && (
                                <div class='mt-2 pt-2 border-top'>
                                    {signupFields.map(field => (
                                        userSignup.signupData[field.name] && (
                                            <p class='card-text text-body-secondary small mb-1' key={field.name}>
                                                <strong>{field.label}:</strong> {userSignup.signupData[field.name]}
                                            </p>
                                        )
                                    ))}
                                </div>
                            )}
                        </div>

                        <div>
                            {isSignedUp ? (
                                <button
                                    class='btn btn-outline-danger rounded-pill'
                                    onClick={this.handleCancel}
                                    disabled={loading}
                                >
                                    {loading ? (
                                        <span class='spinner-border spinner-border-sm' role='status'>
                                            <span class='visually-hidden'>Loading...</span>
                                        </span>
                                    ) : (
                                        <>
                                            <i class='fa-solid fa-xmark me-1' />
                                            <Text id='course.cancelSignup'>Meld av</Text>
                                        </>
                                    )}
                                </button>
                            ) : canSignUp ? (
                                <button
                                    class='btn btn-primary rounded-pill'
                                    onClick={!user.id ? this.promptLogin : (this.userNeedsName() ? this.startNameForm : (hasSignupFields ? this.startSignup : this.handleSignUp))}
                                    disabled={loading}
                                >
                                    {loading ? (
                                        <span class='spinner-border spinner-border-sm' role='status'>
                                            <span class='visually-hidden'>Loading...</span>
                                        </span>
                                    ) : (
                                        <>
                                            <i class='fa-solid fa-plus me-1' />
                                            <Text id='course.signUp'>Meld meg på</Text>
                                        </>
                                    )}
                                </button>
                            ) : (
                                <button class='btn btn-secondary rounded-pill' disabled>
                                    <Text id='course.full'>Fullt</Text>
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

export default CourseSignupCard;
